// ENG-027: when to ask "How did the assistant do in this conversation?".
//
// Not after every answer: only after an answer that closes the question (agent verdict on the
// end frame, or the text rule for n8n), once the user has been idle: 4 s when the gateway gave the
// verdict, 12 s when only the frontend text rule did (n8n). Typing or sending
// cancels the timer and takes an open prompt off the screen; the next answer that closes re-arms
// it, unless the session was already answered. Outside the widget, when the host
// page moves to another product while such an answer is waiting, the prompt shows at once. In the
// widget (ENG-054 product threads) a product change is a thread switch instead: the armed prompt is
// dropped (reset() from useChatPersistence on the thread change) and never shows in the new thread,
// because the conversation it asks about is no longer on screen. Once the user
// picks a rating or closes the prompt it never comes back for that session (in memory plus a
// sessionStorage key per session id); a new conversation has a new session id, so it can ask
// again. Never while an answer is loading or streaming.

import { useCallback, useEffect, useRef, useState } from "react";
import type { FeedbackClosure, RatingPromptTrigger } from "../../api/feedback";
import { getCurrentProductSku } from "../../utils/url";

// Gateway verdict (agent backend): the answer text was classified server-side, ask soon.
export const RATING_PROMPT_IDLE_MS = 4_000;
// Frontend text rule only (n8n backend): less certain, give the user more room.
export const RATING_PROMPT_IDLE_FALLBACK_MS = 12_000;
const ASKED_KEY_PREFIX = "rating-asked:";

export interface RatingPromptTarget {
    sessionKey: string;
    answerIndex: number;
    closure: FeedbackClosure;
    trigger: RatingPromptTrigger;
}

type Armed = Omit<RatingPromptTarget, "trigger"> & { productSku: string | null };

const readAsked = (sessionKey: string): boolean => {
    try {
        return sessionStorage.getItem(ASKED_KEY_PREFIX + sessionKey) === "1";
    } catch {
        return false;
    }
};

const writeAsked = (sessionKey: string): void => {
    try {
        sessionStorage.setItem(ASKED_KEY_PREFIX + sessionKey, "1");
    } catch {
        // Storage blocked (private mode, sandboxed WebView): the in-memory flag still holds.
    }
};

// `productThreads`: the widget, where a host product change switches the conversation thread.
export function useRatingPrompt(busy: boolean, productThreads = false) {
    const [prompt, setPrompt] = useState<RatingPromptTarget | null>(null);
    const armedRef = useRef<Armed | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const askedRef = useRef<Set<string>>(new Set());
    const busyRef = useRef(busy);
    busyRef.current = busy;
    const promptRef = useRef(prompt);
    promptRef.current = prompt;

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const wasAsked = useCallback((sessionKey: string) => askedRef.current.has(sessionKey) || readAsked(sessionKey), []);

    const show = useCallback(
        (trigger: RatingPromptTrigger) => {
            clearTimer();
            const armed = armedRef.current;
            armedRef.current = null;
            if (!armed || busyRef.current || promptRef.current || wasAsked(armed.sessionKey)) return;
            setPrompt({ sessionKey: armed.sessionKey, answerIndex: armed.answerIndex, closure: armed.closure, trigger });
        },
        [clearTimer, wasAsked]
    );

    // An answer finished. `closure` is null when it was aborted or failed.
    const onAnswerComplete = useCallback(
        (sessionKey: string, answerIndex: number, closure: FeedbackClosure | null) => {
            clearTimer();
            armedRef.current = null;
            if (!closure || !closure.closes || !sessionKey || wasAsked(sessionKey)) return;
            armedRef.current = { sessionKey, answerIndex, closure, productSku: getCurrentProductSku() };
            const idleMs = closure.source === "gateway" ? RATING_PROMPT_IDLE_MS : RATING_PROMPT_IDLE_FALLBACK_MS;
            timerRef.current = setTimeout(() => show("closure_idle"), idleMs);
        },
        [clearTimer, show, wasAsked]
    );

    // The user is typing, dictating or sending: they are not done, so do not interrupt. A prompt
    // already on screen goes away without marking the session: only a choice or the × does that.
    const onUserActivity = useCallback(() => {
        clearTimer();
        armedRef.current = null;
        setPrompt(null);
    }, [clearTimer]);

    // The user answered (any of the three choices) or closed the prompt: never again this session.
    const markAsked = useCallback(() => {
        const current = promptRef.current;
        if (!current) return;
        askedRef.current.add(current.sessionKey);
        writeAsked(current.sessionKey);
    }, []);

    const close = useCallback(() => {
        markAsked();
        setPrompt(null);
    }, [markAsked]);

    // New conversation / site switch / thread switch (ENG-054): drop the timer and anything on screen.
    const reset = useCallback(() => {
        clearTimer();
        armedRef.current = null;
        setPrompt(null);
    }, [clearTimer]);

    // A new request started while the prompt was up and unanswered: hide it without marking
    // the session, so a later answer that closes may ask again.
    useEffect(() => {
        if (busy) {
            clearTimer();
            armedRef.current = null;
            setPrompt(current => (current && !askedRef.current.has(current.sessionKey) ? null : current));
        }
    }, [busy, clearTimer]);

    // The host page moved to another product while a closing answer waits -> ask now. Not with
    // product threads (ENG-054): there the product change is a thread switch, which disarms the
    // prompt (reset) instead of firing "host_context_change".
    useEffect(() => {
        if (productThreads) return;
        const onHostContext = () => {
            const armed = armedRef.current;
            if (armed && getCurrentProductSku() !== armed.productSku) show("host_context_change");
        };
        window.addEventListener("public-widget:host-context", onHostContext);
        return () => window.removeEventListener("public-widget:host-context", onHostContext);
    }, [show, productThreads]);

    useEffect(() => clearTimer, [clearTimer]);

    return { prompt, onAnswerComplete, onUserActivity, markAsked, close, reset };
}
